/**
 * Data layer for `advertiser-payout-rules`. Thin typed fetch wrappers over /api/v1/advertiser-payout-rules.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Shape ported from pre-nuke legacy `apps/dash-legacy/src/routes/_authed/rules/index.tsx`,
 * modernized for plain-React + Tailwind + TanStack Query (no shadcn, no zod).
 */

export type PayoutRuleActionKind = "multiplier" | "flat" | "cap";
export type PayoutRuleOperator = ">" | ">=" | "<" | "<=" | "=" | "!=";
export type PayoutRuleMetric =
	| "clicks"
	| "conversions"
	| "payout"
	| "epc"
	| "cr"
	| "ctr";
export type PayoutRuleStatus = "active" | "paused";

export interface PayoutRuleCondition {
	metric: PayoutRuleMetric;
	operator: PayoutRuleOperator;
	value: number;
}

export interface PayoutRuleAction {
	kind: PayoutRuleActionKind;
	/** multiplier: e.g. 1.1 = +10%; flat: absolute payout amount; cap: max payout. */
	amount: number;
}

export interface AdvertiserPayoutRulesRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	advertiser_name: string;
	name: string;
	condition: PayoutRuleCondition;
	action: PayoutRuleAction;
	effective_from: string | null;
	effective_to: string | null;
	status: PayoutRuleStatus;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListAdvertiserPayoutRulesResponse {
	count: number;
	items: AdvertiserPayoutRulesRow[];
}

export interface CreateAdvertiserPayoutRulesBody {
	advertiser_id: string;
	name: string;
	condition: PayoutRuleCondition;
	action: PayoutRuleAction;
	effective_from?: string | null;
	effective_to?: string | null;
	status?: PayoutRuleStatus;
	[key: string]: unknown;
}

export interface AdvertiserOption {
	id: string;
	name: string;
}

const BASE = "/api/v1/advertiser-payout-rules";
const ADVERTISERS_BASE = "/api/v1/advertisers";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAdvertiserPayoutRulesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAdvertiserPayoutRulesResponse>(res);
}

export async function create(body: CreateAdvertiserPayoutRulesBody): Promise<AdvertiserPayoutRulesRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<AdvertiserPayoutRulesRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

/** Advertiser picker source. Gracefully returns [] on failure so the drawer remains usable. */
export async function listAdvertisers(): Promise<AdvertiserOption[]> {
	try {
		const res = await fetch(ADVERTISERS_BASE, {
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		});
		if (!res.ok) return [];
		const raw = (await res.json()) as unknown;
		if (raw && typeof raw === "object" && "items" in raw) {
			const items = (raw as { items: unknown }).items;
			if (Array.isArray(items)) {
				return items
					.map((x) => {
						if (!x || typeof x !== "object") return null;
						const o = x as Record<string, unknown>;
						const id = typeof o.id === "string" ? o.id : null;
						const name =
							typeof o.name === "string"
								? o.name
								: typeof o.display_name === "string"
									? o.display_name
									: id;
						return id && name ? { id, name } : null;
					})
					.filter((x): x is AdvertiserOption => x !== null);
			}
		}
		return [];
	} catch {
		return [];
	}
}

export const advertiserPayoutRulesQueryKeys = {
	all: ["advertiser-payout-rules"] as const,
	list: () => [...advertiserPayoutRulesQueryKeys.all] as const,
	detail: (id: string) => [...advertiserPayoutRulesQueryKeys.all, id] as const,
	advertisers: () => ["advertiser-payout-rules", "advertisers"] as const,
};
